import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { appEnv } from "../../config/env";
import { registerAccessTokenProvider } from "../../lib/http/client";
import { neonAuthClient, type NeonAuthUser } from "../../lib/auth/neon-client";

const DEMO_SESSION_KEY = "empreitaflow-demo-session";
export const DEMO_EMAIL = "admin@empreitaflow.local";
export const DEMO_PASSWORD = "Empreita@2026";

interface AuthContextValue {
  authenticated: boolean;
  loading: boolean;
  user: NeonAuthUser | null;
  demoMode: boolean;
  login(email: string, password: string, remember: boolean): Promise<boolean>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
registerAccessTokenProvider(() => appEnv.dataMode === "api" ? neonAuthClient.token() : null);

const sessionStorageFor = (remember: boolean): Storage =>
  remember ? localStorage : sessionStorage;

/**
 * Uses demo authentication only in non-production mock mode. API mode uses
 * Neon Auth and exposes its short-lived JWT exclusively through the central
 * HTTP client. Authorization and tenant selection remain backend concerns.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<NeonAuthUser | null>(null);
  const [demoAuthenticated, setDemoAuthenticated] = useState(false);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      if (appEnv.demoAuthEnabled) {
        const authenticated =
          localStorage.getItem(DEMO_SESSION_KEY) === "active" ||
          sessionStorage.getItem(DEMO_SESSION_KEY) === "active";
        if (active) setDemoAuthenticated(authenticated);
        setLoading(false);
        return;
      }
      if (appEnv.dataMode !== "api") {
        setLoading(false);
        return;
      }
      try {
        const session = await neonAuthClient.session();
        if (active) setUser(session?.user ?? null);
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    void initialize();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, remember: boolean) => {
      if (appEnv.demoAuthEnabled) {
        const valid =
          email.trim().toLowerCase() === DEMO_EMAIL &&
          password === DEMO_PASSWORD;
        if (valid) {
          localStorage.removeItem(DEMO_SESSION_KEY);
          sessionStorage.removeItem(DEMO_SESSION_KEY);
          sessionStorageFor(remember).setItem(DEMO_SESSION_KEY, "active");
          setDemoAuthenticated(true);
        }
        return valid;
      }
      if (appEnv.dataMode !== "api") return false;
      try {
        const authenticated = await neonAuthClient.signIn(
          email.trim().toLowerCase(),
          password,
        );
        setUser(authenticated);
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    localStorage.removeItem(DEMO_SESSION_KEY);
    sessionStorage.removeItem(DEMO_SESSION_KEY);
    setDemoAuthenticated(false);
    setUser(null);
    if (appEnv.dataMode === "api") {
      try {
        await neonAuthClient.signOut();
      } catch {
        // Local session is invalidated even if the remote revoke is unavailable.
      }
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      authenticated: appEnv.demoAuthEnabled
        ? demoAuthenticated
        : Boolean(user),
      loading,
      user,
      demoMode: appEnv.demoAuthEnabled,
      login,
      logout,
    }),
    [demoAuthenticated, loading, login, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
};
